import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';

/**
 * THE ONE RULE for which appointment profile the Calendar uses for a
 * practitioner's free slots (Samer's bug, 19 Oct; rule confirmed by Andrew):
 *
 *   1. Only the profiles of the VIEWED practice, not archived.
 *   2. The preferred one.
 *   3. If none is preferred: the first ordered by location (Office, Virtual,
 *      Home, then any other), then profileName, then id.
 *   4. If there is no profile: none, so 0 slots and the state
 *      "No appointment profile at this practice".
 *
 * The backend (PM calendarSlots) applies the same rule and returns its pick as
 * `slotProfileId`. When that id is present AND belongs to the viewed practice,
 * it wins; otherwise the rule runs here.
 *
 * Every Calendar consumer (card count, slot popover, Next chips, quick-book
 * chips) reads its profile from these helpers, so they cannot disagree.
 */

/** Shown on the card and in the popover when the practitioner has no profile at the viewed practice. */
export const NO_PRACTICE_PROFILE_TEXT = 'No appointment profile at this practice';

const LOCATION_RANK: Record<string, number> = { office: 0, virtual: 1, home: 2 };

function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Ordinal compare on upper-cased text: the client twin of .NET
 * `StringComparer.OrdinalIgnoreCase`, which the PM backend (#964) sorts with.
 * Not `localeCompare`: that is culture-aware and orders "_" and case differently.
 */
export function compareOrdinalIgnoreCase(a: string | null | undefined, b: string | null | undefined): number {
  const x = (a ?? '').toUpperCase();
  const y = (b ?? '').toUpperCase();
  return x < y ? -1 : x > y ? 1 : 0;
}

function locationRank(p: CreateAppointmentProfileRequest): number {
  const name = (p.location?.name ?? '').trim().toLowerCase();
  return LOCATION_RANK[name] ?? 3;
}

/** The practitioner's non-archived profiles at `practiceId`, in the rule's order. */
export function profilesAtPractice(
  profiles: CreateAppointmentProfileRequest[] | null | undefined,
  practiceId: string | null | undefined,
): CreateAppointmentProfileRequest[] {
  if (!profiles || profiles.length === 0 || !practiceId) return [];
  return profiles
    .filter((p) => !!p && sameId(p.practiceId, practiceId) && p.isArchived !== true)
    .sort(
      (a, b) =>
        locationRank(a) - locationRank(b) ||
        compareOrdinalIgnoreCase(a.profileName, b.profileName) ||
        compareOrdinalIgnoreCase(a.id, b.id),
    );
}

/**
 * The profile the Calendar uses for this practitioner's slots at `practiceId`,
 * or null when there is none (render the "no profile" state, 0 slots).
 */
export function chooseSlotProfile(
  profiles: CreateAppointmentProfileRequest[] | null | undefined,
  practiceId: string | null | undefined,
  slotProfileId?: string | null,
): CreateAppointmentProfileRequest | null {
  const pool = profilesAtPractice(profiles, practiceId);
  if (pool.length === 0) return null;
  if (slotProfileId) {
    const fromServer = pool.find((p) => sameId(p.id, slotProfileId));
    if (fromServer) return fromServer;
  }
  return pool.find((p) => p.isPreferred === true) ?? pool[0];
}
